import { useEffect, useMemo, useRef, useState } from 'react'

import {
  type ReadAloudLearner,
  type Reading,
  type Refusal,
  type SubmitOutcome,
} from '@/platform/read-aloud/api'
import { findSentence, PASSAGES } from '@/platform/read-aloud/passages'
import {
  MAX_RECORDING_SECONDS,
  type Recorder,
  type RecorderHandle,
  type Recording,
  type RecordingProblem,
} from '@/platform/read-aloud/recorder'
import { newUuid } from '@/shared/id/uuid'

/**
 * Read Aloud for the learner (FR-70–FR-78, KT1 integrated). Choose a sentence,
 * record, listen back, send; the result shows at most five words to practise
 * (FR-75, FR-76) — never a score, a percentage or what Azure heard (FR-73).
 * When there is no usable evidence the screen says it could not assess,
 * instead of inventing a problem. Every reading counts as practice (FR-77).
 *
 * The recording stays in this browser tab for replay; it is sent once to the
 * server for assessment and not stored there. History comes from the database.
 * The sentences are unreviewed pilot content (D-20) and labelled as such.
 */

export interface ReadAloudScreenProps {
  readonly learner: ReadAloudLearner
  readonly recorder: Recorder
  readonly onHome: () => void
  /** Object URL for replaying a recording; injectable for tests. */
  readonly audioUrl?: (wav: ArrayBuffer) => string | null
  readonly newId?: () => string
}

const BTN = 'h-14 rounded-2xl px-5 text-lg font-semibold disabled:opacity-50'
const PRIMARY = `${BTN} bg-emerald-700 text-white`
const SECONDARY = `${BTN} border-2 border-slate-300 dark:border-slate-700`

const PILOT_LABEL = 'Bản thử nghiệm: các câu luyện đọc này chưa được thầy cô duyệt.'

const PROBLEM_TEXT: Record<RecordingProblem, string> = {
  permission_denied:
    'Ứng dụng chưa được dùng micro. Con nhờ bố mẹ cho phép dùng micro trong trình duyệt nhé.',
  no_microphone: 'Không tìm thấy micro trên máy này. Con nhờ bố mẹ kiểm tra giúp nhé.',
  interrupted: 'Việc ghi âm bị ngắt giữa chừng. Con đọc lại nhé.',
  empty: 'Chưa nghe thấy gì. Con bấm nút micro rồi đọc to câu này nhé.',
  failed: 'Chưa ghi âm được. Con thử lại nhé.',
}

const REFUSAL_TEXT: Record<Refusal, string> = {
  consent_required:
    'Bố mẹ cần đồng ý cho ứng dụng gửi giọng đọc tới dịch vụ Azure Speech trước. Con nhờ bố mẹ xem nhé.',
  not_available: 'Phần Đọc to chưa mở cho gia đình mình.',
  learner_session_invalid: 'Con cần nhập lại mã PIN để tiếp tục.',
  not_authenticated: 'Con cần nhờ bố mẹ đăng nhập lại.',
  invalid_audio: 'Bản ghi này chưa dùng được. Con đọc lại nhé.',
  too_large: 'Bản ghi này quá dài. Con đọc lại một câu thôi nhé.',
  bad_request: 'Có lỗi khi gửi. Con thử lại nhé.',
  conflict: 'Có lỗi khi gửi. Con đọc lại nhé.',
}

interface Target {
  readonly passageId: string
  readonly sentenceIndex: number
  readonly text: string
}

type Stage =
  | { kind: 'loading' }
  | { kind: 'closed'; why: 'not_available' | 'consent_required' | 'unreachable' }
  | { kind: 'pick' }
  | { kind: 'history'; readings: Reading[] | null; failed: boolean }
  | { kind: 'ready'; target: Target; problem: RecordingProblem | null }
  | { kind: 'recording'; target: Target }
  | { kind: 'review'; target: Target; recording: Recording; attemptId: string }
  | { kind: 'checking'; target: Target; recording: Recording; attemptId: string }
  | {
      kind: 'outcome'
      target: Target
      recording: Recording
      attemptId: string
      outcome: SubmitOutcome
    }

function defaultAudioUrl(wav: ArrayBuffer): string | null {
  return typeof URL.createObjectURL === 'function'
    ? URL.createObjectURL(new Blob([wav], { type: 'audio/wav' }))
    : null
}

export function ReadAloudScreen({
  learner,
  recorder,
  onHome,
  audioUrl = defaultAudioUrl,
  newId = newUuid,
}: ReadAloudScreenProps): React.JSX.Element {
  const [stage, setStage] = useState<Stage>({ kind: 'loading' })
  const handle = useRef<RecorderHandle | null>(null)
  const [elapsed, setElapsed] = useState(0)

  useEffect(() => {
    let active = true
    learner.status().then(
      (s) => {
        if (!active) return
        setStage(
          !s.available
            ? { kind: 'closed', why: 'not_available' }
            : !s.consent
              ? { kind: 'closed', why: 'consent_required' }
              : { kind: 'pick' },
        )
      },
      () => {
        if (active) setStage({ kind: 'closed', why: 'unreachable' })
      },
    )
    return () => {
      active = false
    }
  }, [learner])

  // Recording timer.
  useEffect(() => {
    if (stage.kind !== 'recording') return
    const started = Date.now()
    const id = setInterval(() => {
      setElapsed(
        Math.min(MAX_RECORDING_SECONDS, Math.floor((Date.now() - started) / 1000)),
      )
    }, 250)
    return () => {
      clearInterval(id)
    }
  }, [stage.kind])

  // Stop the microphone if the learner leaves the screen.
  useEffect(
    () => () => {
      handle.current?.cancel()
    },
    [],
  )

  const choose = (passageId: string, sentenceIndex: number) => {
    const s = findSentence(passageId, sentenceIndex)
    if (s !== null)
      setStage({
        kind: 'ready',
        target: { passageId, sentenceIndex, text: s.text },
        problem: null,
      })
  }

  const record = (target: Target) => {
    setElapsed(0)
    setStage({ kind: 'recording', target })
    // Synchronous inside the tap: required for iOS.
    handle.current = recorder((result) => {
      handle.current = null
      setStage(
        result.ok
          ? { kind: 'review', target, recording: result.recording, attemptId: newId() }
          : { kind: 'ready', target, problem: result.problem },
      )
    })
  }

  const send = async (target: Target, recording: Recording, attemptId: string) => {
    setStage({ kind: 'checking', target, recording, attemptId })
    const outcome = await learner.submit({
      clientAttemptId: attemptId,
      passageId: target.passageId,
      sentenceIndex: target.sentenceIndex,
      wav: recording.wav,
    })
    setStage({ kind: 'outcome', target, recording, attemptId, outcome })
  }

  const openHistory = () => {
    setStage({ kind: 'history', readings: null, failed: false })
    learner.history().then(
      (readings) => {
        setStage({ kind: 'history', readings, failed: false })
      },
      () => {
        setStage({ kind: 'history', readings: null, failed: true })
      },
    )
  }

  return (
    <div className="mx-auto flex w-full max-w-md flex-1 flex-col gap-6 px-5 pt-2 pb-16 md:max-w-2xl">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">
          <span aria-hidden>🎤 </span>Đọc to
        </h1>
        <button type="button" className="px-2 text-lg underline" onClick={onHome}>
          Về trang chính
        </button>
      </div>

      {stage.kind === 'loading' && <p className="text-lg">Đang mở…</p>}

      {stage.kind === 'closed' && (
        <p
          role="status"
          className="rounded-2xl bg-amber-50 p-4 text-lg dark:bg-amber-950/40"
        >
          {stage.why === 'unreachable'
            ? 'Chưa kết nối được. Con thử lại sau nhé.'
            : REFUSAL_TEXT[stage.why]}
        </p>
      )}

      {stage.kind === 'pick' && (
        <>
          <p
            role="note"
            className="rounded-xl bg-sky-50 px-3 py-2 text-base dark:bg-sky-950/40"
          >
            {PILOT_LABEL}
          </p>
          <p className="text-lg">Chọn một câu để đọc to.</p>
          {PASSAGES.map((p) => (
            <section key={p.id} aria-label={p.title} className="flex flex-col gap-2">
              <h2 className="text-lg font-semibold" lang="en">
                {p.title}
              </h2>
              {p.sentences.map((s, i) => (
                <button
                  key={s}
                  type="button"
                  lang="en"
                  className="rounded-2xl border-2 border-slate-200 p-4 text-left text-lg dark:border-slate-800"
                  onClick={() => {
                    choose(p.id, i)
                  }}
                >
                  {s}
                </button>
              ))}
            </section>
          ))}
          <button type="button" className={SECONDARY} onClick={openHistory}>
            Các lần đọc trước
          </button>
        </>
      )}

      {stage.kind === 'history' && (
        <History
          stage={stage}
          onBack={() => {
            setStage({ kind: 'pick' })
          }}
        />
      )}

      {(stage.kind === 'ready' ||
        stage.kind === 'recording' ||
        stage.kind === 'review' ||
        stage.kind === 'checking' ||
        stage.kind === 'outcome') && (
        <p
          lang="en"
          className="rounded-2xl bg-slate-50 p-5 text-center text-2xl leading-relaxed font-semibold dark:bg-slate-900"
        >
          {stage.target.text}
        </p>
      )}

      {stage.kind === 'ready' && (
        <>
          {stage.problem !== null && (
            <p role="alert" className="text-lg text-rose-800 dark:text-rose-300">
              {PROBLEM_TEXT[stage.problem]}
            </p>
          )}
          <p className="text-lg">
            Bấm nút micro rồi đọc to câu trên. Đọc xong thì bấm “Đọc xong”. Tối đa{' '}
            {MAX_RECORDING_SECONDS} giây.
          </p>
          <button
            type="button"
            className={PRIMARY}
            onClick={() => {
              record(stage.target)
            }}
          >
            🎤 Bắt đầu đọc
          </button>
          <button
            type="button"
            className={SECONDARY}
            onClick={() => {
              setStage({ kind: 'pick' })
            }}
          >
            Chọn câu khác
          </button>
        </>
      )}

      {stage.kind === 'recording' && (
        <>
          <p role="status" className="text-center text-xl font-semibold text-rose-700">
            <span aria-hidden>🔴 </span>Đang ghi âm… {elapsed} / {MAX_RECORDING_SECONDS}{' '}
            giây
          </p>
          <button
            type="button"
            className={PRIMARY}
            onClick={() => handle.current?.stop()}
          >
            ⏹ Đọc xong
          </button>
          <button
            type="button"
            className={SECONDARY}
            onClick={() => {
              handle.current?.cancel()
              handle.current = null
              setStage({ kind: 'ready', target: stage.target, problem: null })
            }}
          >
            Hủy
          </button>
        </>
      )}

      {stage.kind === 'review' && (
        <>
          <Replay wav={stage.recording.wav} audioUrl={audioUrl} />
          {stage.recording.truncated && (
            <p className="text-base">Bản ghi dừng ở {MAX_RECORDING_SECONDS} giây.</p>
          )}
          <button
            type="button"
            className={PRIMARY}
            onClick={() => void send(stage.target, stage.recording, stage.attemptId)}
          >
            Gửi bài đọc
          </button>
          <button
            type="button"
            className={SECONDARY}
            onClick={() => {
              record(stage.target)
            }}
          >
            Đọc lại
          </button>
        </>
      )}

      {stage.kind === 'checking' && (
        <p role="status" className="text-center text-lg">
          Đang nghe bài đọc của con…
        </p>
      )}

      {stage.kind === 'outcome' && (
        <Outcome
          outcome={stage.outcome}
          wav={stage.recording.wav}
          audioUrl={audioUrl}
          onRetrySend={() => void send(stage.target, stage.recording, stage.attemptId)}
          onPractiseAgain={() => {
            setStage({ kind: 'ready', target: stage.target, problem: null })
          }}
          onOther={() => {
            setStage({ kind: 'pick' })
          }}
        />
      )}
    </div>
  )
}

function Replay({
  wav,
  audioUrl,
}: {
  wav: ArrayBuffer
  audioUrl: (wav: ArrayBuffer) => string | null
}): React.JSX.Element | null {
  const url = useMemo(() => audioUrl(wav), [wav, audioUrl])
  useEffect(
    () => () => {
      if (url !== null && typeof URL.revokeObjectURL === 'function')
        URL.revokeObjectURL(url)
    },
    [url],
  )
  if (url === null) return null
  return (
    <div className="flex flex-col gap-1">
      <p className="text-lg font-semibold">Nghe lại giọng con</p>
      <audio controls src={url} aria-label="Nghe lại giọng con" className="w-full" />
    </div>
  )
}

function Outcome({
  outcome,
  wav,
  audioUrl,
  onRetrySend,
  onPractiseAgain,
  onOther,
}: {
  outcome: SubmitOutcome
  wav: ArrayBuffer
  audioUrl: (wav: ArrayBuffer) => string | null
  onRetrySend: () => void
  onPractiseAgain: () => void
  onOther: () => void
}): React.JSX.Element {
  const retry =
    outcome.kind === 'network' || (outcome.kind === 'not_assessed' && outcome.retryable)
  return (
    <>
      <section aria-label="Kết quả" className="flex flex-col gap-3">
        <OutcomeMessage outcome={outcome} />
      </section>
      <Replay wav={wav} audioUrl={audioUrl} />
      {retry && (
        <button type="button" className={PRIMARY} onClick={onRetrySend}>
          Thử gửi lại
        </button>
      )}
      <button
        type="button"
        className={retry ? SECONDARY : PRIMARY}
        onClick={onPractiseAgain}
      >
        Luyện lại câu này
      </button>
      <button type="button" className={SECONDARY} onClick={onOther}>
        Chọn câu khác
      </button>
    </>
  )
}

function OutcomeMessage({ outcome }: { outcome: SubmitOutcome }): React.JSX.Element {
  switch (outcome.kind) {
    case 'result': {
      const r = outcome.reading
      if (r.assessment_status === 'no_speech')
        return (
          <p role="status" className="text-lg">
            Ứng dụng chưa nghe rõ giọng con nên chưa nhận xét được. Con thử đọc lại, gần
            micro hơn một chút nhé.
          </p>
        )
      if (r.word_flags.length === 0)
        return (
          <p role="status" className="text-lg">
            <span aria-hidden>🌟 </span>Con đã đọc xong câu này. Lần này không có từ nào
            cần luyện thêm.
          </p>
        )
      return (
        <>
          <p role="status" className="text-lg">
            <span aria-hidden>🌟 </span>Con đã đọc xong! Con thử đọc lại thật rõ những từ
            này nhé:
          </p>
          <ul className="flex flex-wrap gap-2" aria-label="Từ cần luyện">
            {r.word_flags.map((f) => (
              <li
                key={f.word}
                lang="en"
                className="rounded-xl bg-amber-100 px-4 py-2 text-xl font-semibold dark:bg-amber-900/50"
              >
                {f.word}
              </li>
            ))}
          </ul>
          <p className="text-base text-slate-600 dark:text-slate-400">
            Đây là gợi ý cho lần đọc này, không phải điểm số.
          </p>
        </>
      )
    }
    case 'not_assessed':
      return (
        <p role="status" className="text-lg">
          Bài đọc của con đã được ghi nhận, nhưng lúc này ứng dụng chưa kiểm tra được. Đây
          không phải lỗi của con.
        </p>
      )
    case 'network':
      return (
        <p role="alert" className="text-lg">
          Chưa gửi được bài đọc. Con kiểm tra mạng rồi thử gửi lại nhé.
        </p>
      )
    case 'refused':
      return (
        <p role="alert" className="text-lg">
          {REFUSAL_TEXT[outcome.why]}
        </p>
      )
  }
}

const STATUS_TEXT: Record<Reading['assessment_status'], string> = {
  assessed: 'Đã nhận xét',
  no_speech: 'Chưa nghe rõ',
  pending: 'Đang kiểm tra',
  failed_retryable: 'Chưa kiểm tra được',
  failed: 'Chưa kiểm tra được',
}

function History({
  stage,
  onBack,
}: {
  stage: Extract<Stage, { kind: 'history' }>
  onBack: () => void
}): React.JSX.Element {
  return (
    <section aria-label="Các lần đọc trước" className="flex flex-col gap-3">
      <h2 className="text-xl font-bold">Các lần đọc trước</h2>
      {stage.failed ? (
        <p role="alert">Chưa tải được. Con thử lại sau nhé.</p>
      ) : stage.readings === null ? (
        <p>Đang tải…</p>
      ) : stage.readings.length === 0 ? (
        <p>Con chưa có lần đọc nào.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {stage.readings.map((r) => (
            <li
              key={r.client_attempt_id}
              className="rounded-2xl border border-slate-200 p-3 dark:border-slate-800"
            >
              <p lang="en" className="text-lg">
                {findSentence(r.passage_id, r.sentence_index)?.text ?? '—'}
              </p>
              <p className="text-base text-slate-600 dark:text-slate-400">
                {r.created_at === undefined
                  ? ''
                  : `${new Date(r.created_at).toLocaleDateString('vi-VN')} · `}
                {STATUS_TEXT[r.assessment_status]}
              </p>
              {r.word_flags.length > 0 && (
                <p className="text-base">
                  Từ đã luyện:{' '}
                  <span lang="en">{r.word_flags.map((f) => f.word).join(', ')}</span>
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
      <p className="text-base text-slate-600 dark:text-slate-400">
        Mỗi lần đọc là một lần luyện tập; một lần đọc không nói lên con giỏi hay yếu.
      </p>
      <button type="button" className={SECONDARY} onClick={onBack}>
        Quay lại
      </button>
    </section>
  )
}
